package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.TranslationState
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LArrowDownIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LChord
import dev.lingvo.site.ds.LDangerIcon
import dev.lingvo.site.ds.LCircleCheckIcon
import dev.lingvo.site.ds.LEllipsisIcon
import dev.lingvo.site.ds.LEyeIcon
import dev.lingvo.site.ds.LHistoryIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LMenu
import dev.lingvo.site.ds.LMenuFocusPolicy
import dev.lingvo.site.ds.LMenuItem
import dev.lingvo.site.ds.LSparkleIcon
import dev.lingvo.site.ds.LTrashIcon
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.chordKeys
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.commandChord
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun EditorActionBar(
    state: TranslationEditorState,
    row: KeyRow,
    draft: EditorDraft,
    onOpenOverflow: (EditorFocusRef) -> Unit = {},
    onCloseOverflow: () -> Unit = {},
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit = { _, _ -> },
    onOpenModal: (EditorModalSurface, EditorFocusRef) -> Unit = { _, _ -> },
    /** See `TranslationPane.compactActions`. */
    compactActions: Boolean = false,
) {
    if (!state.capability.canWriteTranslations) return
    val findings = mergedQa(
        live = inlineQa(row, draft, state.locale),
        remote = state.qaIssues.filter { it.keyId == row.key.id },
    )
    val firstError = findings.firstOrNull { it.severity == QaSeverity.ERROR }
    val domBridge = LocalEditorDomBridge.current
    // Spec §7.1: the primary is "Approve and next" only when the draft is QA-clean (which is also
    // what makes it non-empty) and the reader may approve; otherwise it is "Save and next".
    val actions = editorActions(
        approveReady = firstError == null && state.capability.canApproveTranslations,
    ).filter { action ->
        !action.approve || state.capability.canApproveTranslations
    }

    Column(EditorActionBarStyle.toModifier().attr("data-editor-action-bar", "true")) {
        // Was gated on `mode == REVIEW`: approving is no longer review-only, so the reason it is
        // blocked has to be visible wherever the Approve button now lives.
        if (firstError != null) {
            val errorCount = findings.count { it.severity == QaSeverity.ERROR }
            // **The fragment is what "Show" has to show.** With none — and "Translation is empty."
            // is the queue's most common error and never has one — the button re-focused the field
            // the caret was already in, which is a control that does nothing.
            val errorFragment = firstError.fragment?.takeIf(String::isNotBlank)
            // **The strip earns its line by adding something.** The identical sentence is already
            // drawn as the inline finding under the target field; repeating it verbatim two
            // hundred pixels lower says the same fact twice on the state a translator is in most
            // of the time. It repeats the message when the strip carries more than the message —
            // a run to jump to, or a count of the errors the reader has not seen — and otherwise
            // says the one thing the inline finding does not: what the error is costing them.
            val stripAddsEvidence = errorFragment != null || errorCount > 1
            Row(
                EditorQaBlockerStyle.toModifier().attr("id", EDITOR_QA_BLOCKER_ID),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // The glyph is the severity, said once. Never colour alone: the sentence beside it
                // is the whole message and the icon is `aria-hidden` inside it.
                // **The glyph has to be told its size, not just its box.** `LIconStyle` draws every
                // icon at `IconSize.XL` unless the call site says otherwise, so a bare
                // `LDangerIcon()` inside a 14px span rendered a 20px octagon that overflowed six
                // pixels to the right and touched the "A" of its own sentence — the gap beside it
                // could not fix a glyph that was wider than the space it claimed.
                Span(Modifier.iconSize(IconSize.SM).toAttrs { attr("aria-hidden", "true") }) {
                    LDangerIcon(Modifier.iconSize(IconSize.SM))
                }
                Span(Modifier.toAttrs()) {
                    Text(
                        if (stripAddsEvidence) {
                            firstError.message
                        } else {
                            t(
                                "editor.editor_action_bar.approve_is_blocked_until_the",
                                "Approve is blocked until the error above is fixed.",
                            )
                        },
                    )
                }
                // **Which one of how many.** A strip that names one error while three exist reads
                // as "fix this and you are done", and the second press of Approve is refused for a
                // reason the reader has not been shown.
                if (errorCount > 1) {
                    Span(EditorMetaStyle.toModifier().toAttrs()) {
                        Text(
                            tf(
                                "editor.editor_action_bar.one_of_count",
                                "(1 of {count})",
                                "count" to errorCount.toString(),
                            ),
                        )
                    }
                }
                // **Naming the error is not showing it.** The strip says "Placeholder {count} is
                // missing" over a paragraph the reader now has to scan by eye — and on a plural key
                // the offending form may not even be the one on screen. "Show" puts the caret in
                // the target and selects the run itself, which is the only scroll a `<textarea>`
                // offers; there are no child nodes inside one to reveal.
                //
                // It is a real button rather than the strip being clickable: a `role="note"` band
                // that silently moves focus when clicked is a control with no name, and the two
                // things in this strip — read the reason, go to it — are not the same action.
                if (errorFragment != null) {
                    LButton(
                        label = t("editor.editor_action_bar.show", "Show"),
                        onClick = {
                            activeEditorTarget(state)?.let { target ->
                                domBridge.revealTargetFragment(
                                    target = target,
                                    fragment = errorFragment,
                                )
                            }
                        },
                        modifier = Modifier
                            .then(EditorCoarseTargetStyle.toModifier())
                            .attr("data-editor-coarse-target", "tool")
                            .attr("data-editor-qa-blocker-show", "true"),
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                        accessibleLabel = t(
                            "editor.editor_action_bar.show_where_this_qa_error_is",
                            "Show where this QA error is in the translation",
                        ),
                    )
                }
            }
        }
        Row(
            EditorActionBarRowStyle.toModifier()
                .attr("data-editor-action-row", "true"),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Div(Modifier.attr("data-editor-action-metadata", "true").toAttrs()) {
                // **The count is said once, where the typing is.** `EditorBudgetMeter` draws
                // "9 / 24 characters" under the target field; the dock drew the same fact again
                // 400px away, and in the far more common no-maxLength case it drew the strictly
                // poorer version of it — a bare "0 characters", which the meter's own KDoc argues
                // says nothing. The save status is what this cluster is for, and at 320px it now
                // gets the room the count was taking.
                Span(EditorSaveStatusStyle.toModifier().toAttrs()) {
                    Text(editorSaveStatus(draft))
                }
            }
            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                actions.forEachIndexed { index, action ->
                    // The first action keeps the identity two existing DOM contracts
                    // (`TranslationPaneSurfaceTest`, `EditorControllerGeometryTest`) and
                    // `EditorControllerDom`'s own evidence hook already read off it: exactly one
                    // `data-editor-primary` element, styled `PRIMARY`. `editorActions` orders each
                    // mode so its own former single action stays first — this is the addition, not
                    // a change to what "primary" already meant.
                    val primary = index == 0
                    // The blocker is Approve's alone: Save never needed a clean QA pass and still
                    // does not, whichever position it renders in.
                    val blocked = action.approve && firstError != null
                    val actionModifier = Modifier
                        .then(EditorCoarseTargetStyle.toModifier())
                        .attr("data-editor-coarse-target", if (primary) "primary" else "secondary")
                        .then(if (primary) Modifier.attr("data-editor-primary", "true") else Modifier)
                        // **`aria-disabled`, not `enabled = false`.** The native `disabled`
                        // attribute takes the button out of the tab order, so on the most
                        // common key state — an untranslated string — a keyboard reader met
                        // one action, was never told a second existed, and could not reach
                        // the sentence saying why. `LButton` already paints `aria-disabled`
                        // exactly like real disabled for `loading`; this is the same fact
                        // about the button, said the same way, plus the reason.
                        .then(
                            if (blocked) {
                                // And painted the way the design system paints anything a reader
                                // cannot press: `LButton` swaps a filled variant to the neutral
                                // pair when it is unavailable, but keys that off `enabled = false`
                                // — the one thing this button must not be, for the reason above.
                                // So the refusal keeps its place in the tab order *and* the
                                // contrast; see `EditorRefusedActionStyle`.
                                EditorRefusedActionStyle.toModifier()
                                    .attr("data-editor-refusal", "true")
                                    .attr("aria-disabled", "true")
                                    .attr("aria-describedby", EDITOR_QA_BLOCKER_ID)
                            } else {
                                Modifier
                            },
                        )
                    val onAction = {
                        if (!blocked) state.saveCurrent(approve = action.approve, advance = true)
                    }
                    // The glyph is the verb: a double check for "this is signed off", a single
                    // one for "this is written down". At the compact width it is the only thing
                    // that keeps the two apart, because at that width it is all there is.
                    val actionIcon: @Composable () -> Unit = {
                        if (action.approve) LCircleCheckIcon() else LCheckIcon()
                    }
                    Row(Modifier.gap(Space.XS), verticalAlignment = Alignment.CenterVertically) {
                        if (compactActions) {
                            // **Below 640px the label moves into the tooltip, and it is the width
                            // that decides.**
                            //
                            // `matchMedia` was the first attempt and is the wrong instrument here:
                            // it measures the window, while this dock is sized by its pane. The
                            // editor already answers this question the right way for its header,
                            // from `viewportWidth`, so this asks it the same way. At 320px even the
                            // shortened one-word labels wrapped inside a 40px button and were
                            // clipped, and on Linux, where the same words render a few pixels
                            // wider, they overflowed the row outright.
                            //
                            // `LIconAction` is the shape this has to take rather than a bare icon
                            // button: an icon-only control owes the reader a name, and here it owes
                            // it the *full* name — "Approve and next", not the "Approve" the wide
                            // dock had already shortened to fit. A tooltip has room the dock never
                            // had, so the compact reader is the one who is told the most.
                            LIconAction(
                                label = action.label,
                                onClick = onAction,
                                modifier = actionModifier,
                                variant = if (primary) LButtonVariant.PRIMARY else LButtonVariant.SECONDARY,
                                size = LButtonSize.SM,
                                loading = draft.saving,
                                shortcut = if (action.approve) commandChord("⇧Enter") else commandChord("Enter"),
                                icon = actionIcon,
                            )
                        } else {
                            LButton(
                                label = action.label,
                                onClick = onAction,
                                modifier = actionModifier,
                                variant = if (primary) LButtonVariant.PRIMARY else LButtonVariant.SECONDARY,
                                size = LButtonSize.SM,
                                leadingIcon = actionIcon,
                                loading = draft.saving,
                            )
                        }
                        // **No shortcut chips where there is no keyboard.**
                        //
                        // These are what actually filled the 52px dock, not the labels: measured at
                        // 320px, the action block was 256px with "Approve and next" and 256px with
                        // "Approve" — identical, because "⌘⇧Enter" and "⌘Enter" dominate it. That is
                        // why shortening the labels alone changed nothing and the row still
                        // overflowed on Linux, where the chips render wider.
                        //
                        // A device 320 CSS pixels across is a phone. Spending a third of the action
                        // bar telling its owner about a chord they cannot press, while squeezing
                        // the save status to nothing, is the wrong trade in both directions.
                        if (!compactActions) {
                            // One `LKbd` per key rather than one holding the whole chord: a
                            // screen reader used to read "command shift enter" as a single opaque
                            // token, and the caps drew as one wide slab that dominated the dock.
                            LChord(
                                chordKeys(
                                    if (action.approve) commandChord("⇧Enter") else commandChord("Enter"),
                                ),
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
internal fun EditorOverflowMenu(
    state: TranslationEditorState,
    expanded: Boolean,
    onOpenOverflow: (EditorFocusRef) -> Unit,
    onCloseOverflow: () -> Unit,
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit,
    onOpenModal: (EditorModalSurface, EditorFocusRef) -> Unit,
) {
    if (state.selectedRow == null) return
    val draft = state.selectedDraft
    val invoker = EditorFocusRef.ElementId(EDITOR_OVERFLOW_TRIGGER_ID)
    val actions = buildList {
        if (state.capability.canWriteTranslations && draft != null) {
            add(EditorOverflowAction.SAVE)
            add(EditorOverflowAction.CLEAR)
        }
        add(EditorOverflowAction.NEXT_UNTRANSLATED)
        add(EditorOverflowAction.NEXT_QA_ISSUE)
        add(EditorOverflowAction.ACTIVITY)
        // **The three ways out of the editor, in the one menu that already means "everything
        // else".** They were the last spec §7.1 bullet with nowhere to live: the editor is a
        // full-screen surface with no trail and no rail, so a translator who finished a locale and
        // wanted the review queue had to type the URL. Each is gated on the authority its
        // destination actually needs — Pre-translate spends credits, so a reader who cannot spend
        // them is not shown a door they will be refused at.
        add(EditorOverflowAction.REVIEW_QUEUE)
        add(EditorOverflowAction.QA_PAGE)
        if (state.capability.canSpendCredits) add(EditorOverflowAction.PRETRANSLATE)
        add(EditorOverflowAction.SHORTCUTS)
    }
    val items = actions.map { action ->
        when (action) {
            EditorOverflowAction.SAVE -> LMenuItem(
                t("editor.editor_action_bar.save_now", "Save now"),
                disabled = draft?.saving == true,
            )
            EditorOverflowAction.CLEAR -> LMenuItem(
                label = t("editor.editor_action_bar.clear_translation", "Clear translation..."),
                disabled = draft?.saving == true,
                danger = true,
                icon = { LTrashIcon() },
            )
            // The two queue walks, so the chords are discoverable by somebody who reaches for a
            // menu rather than a keyboard. `shortcut` puts the chord on the row itself, which is
            // how a menu teaches one.
            EditorOverflowAction.NEXT_UNTRANSLATED -> LMenuItem(
                label = t("editor.editor_action_bar.next_untranslated", "Next untranslated"),
                icon = { LArrowDownIcon() },
                shortcut = commandChord("⇧↓"),
            )
            EditorOverflowAction.NEXT_QA_ISSUE -> LMenuItem(
                label = t("editor.editor_action_bar.next_qa_issue", "Next QA issue"),
                icon = { LWarningIcon() },
                shortcut = commandChord("⇧E"),
            )
            EditorOverflowAction.ACTIVITY -> LMenuItem(
                label = t("editor.editor_action_bar.open_activity", "Open activity"),
                icon = { LHistoryIcon() },
            )
            EditorOverflowAction.REVIEW_QUEUE -> LMenuItem(
                label = t("editor.editor_action_bar.review_queue", "Review queue"),
                icon = { LEyeIcon() },
            )
            EditorOverflowAction.QA_PAGE -> LMenuItem(
                label = t("editor.editor_action_bar.qa_page", "QA page"),
                icon = { LWarningIcon() },
            )
            // The ellipsis is the promise that nothing is spent by choosing this row: it opens the
            // page that prices the run and asks. Every other automation entry point in the product
            // spells it the same way.
            EditorOverflowAction.PRETRANSLATE -> LMenuItem(
                label = t("editor.editor_action_bar.pre_translate", "Pre-translate…"),
                icon = { LSparkleIcon() },
            )
            EditorOverflowAction.SHORTCUTS -> LMenuItem(
                t("editor.editor_action_bar.keyboard_shortcuts", "Keyboard shortcuts"),
            )
        }
    }

    LMenu(
        expanded = expanded,
        items = items,
        onExpandedChange = { open ->
            if (open) onOpenOverflow(invoker) else onCloseOverflow()
        },
        onSelect = { index ->
            when (actions.getOrNull(index)) {
                EditorOverflowAction.SAVE -> {
                    onCloseOverflow()
                    state.saveCurrent(approve = false, advance = false)
                }
                EditorOverflowAction.CLEAR -> {
                    onOpenModal(EditorModalSurface.CLEAR_CONFIRMATION, invoker)
                }
                EditorOverflowAction.NEXT_UNTRANSLATED -> {
                    onCloseOverflow()
                    state.advanceTo { row ->
                        row.target == null || row.target?.state == TranslationState.EMPTY
                    }
                }
                EditorOverflowAction.NEXT_QA_ISSUE -> {
                    onCloseOverflow()
                    val flagged = state.qaIssues.mapTo(mutableSetOf()) { it.keyId }
                    state.advanceTo { row -> row.key.id in flagged }
                }
                EditorOverflowAction.ACTIVITY -> {
                    onOpenUtility(EditorUtilitySurface.ACTIVITY, invoker)
                }
                EditorOverflowAction.REVIEW_QUEUE -> {
                    onCloseOverflow()
                    state.navigateTo("/app/projects/${state.projectSlug}/review/${state.locale}")
                }
                EditorOverflowAction.QA_PAGE -> {
                    onCloseOverflow()
                    state.navigateTo("/app/projects/${state.projectSlug}/qa")
                }
                EditorOverflowAction.PRETRANSLATE -> {
                    onCloseOverflow()
                    state.navigateTo("/app/projects/${state.projectSlug}/pretranslate")
                }
                EditorOverflowAction.SHORTCUTS -> {
                    onOpenModal(EditorModalSurface.SHORTCUTS, invoker)
                }
                null -> Unit
            }
        },
        modifier = Modifier,
        focusPolicy = LMenuFocusPolicy.DELEGATED,
    ) { toggle, triggerModifier ->
        LIconAction(
            label = t("editor.editor_action_bar.more_editor_actions", "More editor actions"),
            onClick = toggle,
            modifier = triggerModifier
                .then(EditorCoarseTargetStyle.toModifier())
                .attr("id", EDITOR_OVERFLOW_TRIGGER_ID)
                .attr("data-editor-coarse-target", "header"),
            size = LButtonSize.SM,
        ) {
            LEllipsisIcon()
        }
    }
}

private enum class EditorOverflowAction {
    SAVE,
    CLEAR,
    NEXT_UNTRANSLATED,
    NEXT_QA_ISSUE,
    ACTIVITY,
    REVIEW_QUEUE,
    QA_PAGE,
    PRETRANSLATE,
    SHORTCUTS,
}
